import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router";
import { api } from "@/lib/api-client";
import type { LlmTenantConfig } from "../hooks/useLlmConfig";
import styles from "./JobTypeLlmTab.module.css";

/** A field row for the routing table, sourced from the draft version content. */
export interface LlmFieldRow {
  id: string;
  name: string;
  label: string;
  dataType: string;
  llmRoutingMode?: string;
}

const ROUTING_MODES = [
  { value: "off", label: "Off" },
  { value: "fallback", label: "Fallback on Low Confidence" },
  { value: "preferred", label: "LLM Preferred" },
  { value: "only", label: "LLM Only" },
];

interface JobTypeLlmConfig {
  llmEnabled: boolean;
  llmDefaultRoutingMode: string;
  llmFallbackThreshold: number;
}

interface JobTypeLlmTabProps {
  fieldDefinitions: LlmFieldRow[];
  llmConfig?: JobTypeLlmConfig;
  onSave: (data: Record<string, unknown>) => Promise<void>;
  /** Persist a field's routing mode into the draft version content. */
  onFieldRoutingChange: (fieldId: string, mode: string) => Promise<void>;
}

export function JobTypeLlmTab({
  fieldDefinitions,
  llmConfig,
  onSave,
  onFieldRoutingChange,
}: JobTypeLlmTabProps) {
  const [tenantConfigs, setTenantConfigs] = useState<LlmTenantConfig[]>([]);
  const [tenantConfigLoaded, setTenantConfigLoaded] = useState(false);

  // #A2 — Optimistic local state for the enable toggle. The PATCH to update
  // llmEnabled succeeds before the parent's async refresh() completes, so
  // deriving `enabled` purely from the prop made the UI feel broken (click,
  // nothing happens for ~1s, then it flips). Local state flips immediately;
  // useEffect re-syncs when the parent prop eventually catches up; on save
  // failure we revert to the prior prop value.
  const propEnabled = llmConfig?.llmEnabled ?? false;
  const [enabled, setEnabled] = useState(propEnabled);
  useEffect(() => {
    setEnabled(propEnabled);
  }, [propEnabled]);

  const routingMode = llmConfig?.llmDefaultRoutingMode ?? "off";
  const threshold = llmConfig?.llmFallbackThreshold ?? 0.7;

  // Field-level routing is stored per field definition
  const [fieldRouting, setFieldRouting] = useState<Record<string, string>>({});

  useEffect(() => {
    const initial: Record<string, string> = {};
    for (const fd of fieldDefinitions) {
      initial[fd.id] = fd.llmRoutingMode ?? "off";
    }
    setFieldRouting(initial);
  }, [fieldDefinitions]);

  const loadTenantConfig = useCallback(async () => {
    try {
      const data = await api.get<LlmTenantConfig[]>("/llm/config");
      setTenantConfigs(data);
    } catch {
      setTenantConfigs([]);
    } finally {
      setTenantConfigLoaded(true);
    }
  }, []);

  useEffect(() => {
    loadTenantConfig();
  }, [loadTenantConfig]);

  const hasEnabledProvider = tenantConfigs.some((c) => c.enabled);

  const handleToggle = async () => {
    // #A2 — flip optimistically, revert on save failure. The visual revert
    // is the user-facing error signal; the upstream onSave promise stays
    // rejected so callers can also react (logs, toasts) if they choose.
    const next = !enabled;
    setEnabled(next);
    try {
      await onSave({ llmEnabled: next });
    } catch (e) {
      setEnabled(!next);
      // Surface so devs see it in the console; intentionally not re-thrown
      // because re-throwing inside a checkbox onChange becomes an unhandled
      // rejection (no React error boundary catches event-handler async errors).
      console.error("LLM toggle save failed", e);
    }
  };

  const handleRoutingModeChange = async (mode: string) => {
    await onSave({ llmDefaultRoutingMode: mode });
  };

  const handleThresholdChange = async (value: number) => {
    await onSave({ llmFallbackThreshold: value });
  };

  const handleFieldRoutingChange = async (fieldId: string, mode: string) => {
    const prevMode = fieldRouting[fieldId] ?? "off";
    setFieldRouting((prev) => ({ ...prev, [fieldId]: mode }));
    try {
      await onFieldRoutingChange(fieldId, mode);
    } catch (e) {
      setFieldRouting((prev) => ({ ...prev, [fieldId]: prevMode }));
      console.error("LLM field-routing save failed", e);
    }
  };

  return (
    <div className={`${styles.container} form-compact`}>
      {tenantConfigLoaded && !hasEnabledProvider && (
        <div className={styles.warningBanner} data-testid="no-tenant-llm-banner">
          <span>
            No LLM provider is enabled for this tenant. Add one in the LLM Settings admin page first to unlock LLM features for this job type.
          </span>
          <Link
            to="/admin/llm"
            className={styles.warningBannerLink}
            data-testid="open-llm-settings-link"
          >
            Open LLM Settings →
          </Link>
        </div>
      )}

      {/* Enable toggle */}
      <div className={styles.section}>
        <div className={styles.toggleRow}>
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={enabled}
              onChange={handleToggle}
              data-testid="llm-enable-toggle"
            />
            <span className={styles.toggleSlider} />
          </label>
          <span>Enable LLM for this job type</span>
        </div>
        <p className={styles.infoText}>
          When enabled, fields can use AI-powered OCR enhancement
        </p>
      </div>

      {/* Default routing mode */}
      {enabled && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Default Routing Mode</h3>
          <div className={styles.radioGroup}>
            {ROUTING_MODES.map((m) => (
              <label key={m.value} className={styles.radioLabel}>
                <input
                  type="radio"
                  name="routingMode"
                  value={m.value}
                  checked={routingMode === m.value}
                  onChange={() => handleRoutingModeChange(m.value)}
                />
                {m.label}
              </label>
            ))}
          </div>

          {routingMode === "fallback" && (
            <div className={styles.sliderRow}>
              <input
                type="range"
                className={styles.slider}
                min={0}
                max={1}
                step={0.05}
                value={threshold}
                onChange={(e) => handleThresholdChange(parseFloat(e.target.value))}
                data-testid="fallback-threshold"
              />
              <span className={styles.sliderValue}>{threshold.toFixed(2)}</span>
            </div>
          )}
        </div>
      )}

      {/* Field routing table */}
      {enabled && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Field Routing</h3>
          <table className={styles.fieldTable}>
            <thead>
              <tr>
                <th>Field Name</th>
                <th>Data Type</th>
                <th>Routing Mode</th>
              </tr>
            </thead>
            <tbody>
              {fieldDefinitions.map((fd) => (
                <tr key={fd.id} data-testid={`field-row-${fd.id}`}>
                  <td>{fd.label || fd.name}</td>
                  <td>
                    <span className={styles.dataTypeBadge}>{fd.dataType}</span>
                  </td>
                  <td>
                    <select
                      className={styles.routingSelect}
                      value={fieldRouting[fd.id] ?? "off"}
                      onChange={(e) => handleFieldRoutingChange(fd.id, e.target.value)}
                      data-testid={`field-routing-${fd.id}`}
                    >
                      {ROUTING_MODES.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {fieldDefinitions.length === 0 && (
            <p className={styles.infoText}>No field definitions configured for this job type.</p>
          )}
        </div>
      )}
    </div>
  );
}
